import { useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Eye, EyeOff, GripVertical, ImagePlus, Loader2, Lock, LogOut, Trash2, Undo2, X } from "lucide-react";
import api, { API } from "@/lib/api";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  return new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short", year: "numeric" }).format(d);
};

const STATUS = {
  pending: { label: "In attesa", cls: "bg-amber-100 text-amber-800" },
  confirmed: { label: "Confermata", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rifiutata", cls: "bg-red-100 text-red-700" },
};

export default function AdminPage() {
  const [password, setPassword] = useState("");
  const [bookings, setBookings] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  const fileInput = useRef(null);
  const dragIndex = useRef(null);

  const fetchBookings = async (pw) => {
    const res = await api.post("/admin/bookings", { password: pw });
    setBookings(res.data.bookings);
  };

  const fetchPhotos = async (pw = password) => {
    const res = await api.post("/admin/photos/list", { password: pw });
    setPhotos(res.data.photos || []);
  };

  const toggleVisible = async (p) => {
    try {
      await api.post("/admin/photos/visibility", { password, photo_id: p.id, is_visible: p.is_visible === false });
      toast.success(p.is_visible === false ? "Foto di nuovo visibile in galleria." : "Foto nascosta dalla galleria pubblica.");
      await fetchPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operazione non riuscita");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchBookings(password);
      await fetchPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    setUpdating(id);
    try {
      await api.post("/admin/bookings/status", { password, booking_id: id, status });
      toast.success(
        status === "confirmed" ? "Prenotazione confermata" : status === "rejected" ? "Prenotazione rifiutata" : "Tornata in attesa"
      );
      await fetchBookings(password);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setUpdating(null);
    }
  };

  const uploadPhotos = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(list.length > 1 ? `1/${list.length}` : "1");
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      try {
        const fd = new FormData();
        fd.append("password", password);
        fd.append("label", caption);
        fd.append("file", list[i]);
        await api.post("/admin/photos/upload", fd);
        ok++;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Caricamento non riuscito: ${list[i].name}`);
      }
      if (list.length > 1) setUploading(`${i + 1}/${list.length}`);
    }
    if (ok > 0) {
      toast.success(ok === 1 ? "Foto caricata! Ora è visibile nella galleria del sito." : `${ok} foto caricate! Sono ora visibili nella galleria del sito.`);
    }
    setCaption("");
    await fetchPhotos();
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const deletePhoto = async (id) => {
    try {
      await api.post("/admin/photos/delete", { password, photo_id: id });
      toast.success("Foto eliminata dalla galleria.");
      await fetchPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const dropPhoto = async (targetIdx) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === targetIdx) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    setPhotos(next);
    setSavingOrder(true);
    try {
      await api.post("/admin/photos/reorder", { password, ids: next.map((p) => p.id) });
      toast.success("Ordine della galleria aggiornato.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ordine non salvato");
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-linen px-5 py-24">
      <div className="mx-auto max-w-4xl">
        <p className="font-serif text-3xl text-stone-900">
          Saint <span className="italic text-oro">Anthony</span>
          <span className="ml-3 font-mono text-xs uppercase tracking-[0.25em] text-stone-400">Area riservata</span>
        </p>

        {bookings === null ? (
          <form onSubmit={login} data-testid="admin-login-form" className="mt-10 max-w-sm rounded-sm border border-stone-300/50 bg-white p-8 shadow-xl shadow-stone-900/5">
            <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-stone-500">Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-sm border border-stone-300 bg-linen px-4 py-3 text-sm outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"
              required
            />
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-linen transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50"
            >
              <Lock size={14} /> {loading ? "Accesso…" : "Accedi"}
            </button>
          </form>
        ) : (
          <div className="mt-10">
            <div className="mb-6 flex items-center justify-between">
              <p className="text-sm text-stone-600" data-testid="admin-bookings-count">
                {bookings.length} {bookings.length === 1 ? "richiesta" : "richieste"} di prenotazione
              </p>
              <button
                data-testid="admin-logout-button"
                onClick={() => { setBookings(null); setPassword(""); }}
                className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone-600 transition-colors hover:bg-stone-100"
              >
                <LogOut size={13} /> Esci
              </button>
            </div>

            {bookings.length === 0 ? (
              <p className="rounded-sm border border-stone-300/50 bg-white p-10 text-center text-stone-500" data-testid="admin-empty-state">
                Nessuna richiesta di prenotazione per ora.
              </p>
            ) : (
              <div className="space-y-4" data-testid="admin-bookings-list">
                {bookings.map((b) => (
                  <article key={b.id} data-testid={`admin-booking-${b.id}`} className="rounded-sm border border-stone-300/50 bg-white p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-serif text-xl text-stone-900">{b.name}</p>
                        <p className="mt-1 text-sm text-stone-500">
                          {b.email} · {b.phone}
                        </p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wide ${(STATUS[b.status] || STATUS.pending).cls}`} data-testid={`admin-status-${b.id}`}>
                        {(STATUS[b.status] || STATUS.pending).label}
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 border-t border-stone-200 pt-4 text-sm text-stone-600">
                      <span><strong className="text-stone-800">Check-in:</strong> {fmt(b.checkin)}</span>
                      <span><strong className="text-stone-800">Check-out:</strong> {fmt(b.checkout)}</span>
                      <span><strong className="text-stone-800">Ospiti:</strong> {b.guests}</span>
                      <span className="text-stone-400">Ricevuta il {fmt(b.created_at)}</span>
                    </div>
                    {b.pricing && (
                      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-sm bg-linen p-3 text-sm text-stone-600" data-testid={`admin-pricing-${b.id}`}>
                        <span><strong className="text-stone-800">Totale:</strong> {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(b.pricing.total)}</span>
                        <span>Tariffa: {b.pricing.rate_type === "nonrefundable" ? "Non rimborsabile (−20%)" : "Flessibile"}</span>
                        {b.pricing.spa_total > 0 && <span>SPA: {(b.spa_days || []).length} {(b.spa_days || []).length === 1 ? "giorno" : "giorni"}</span>}
                        {b.pricing.pets_total > 0 && <span>Animali: {b.pricing.pets === "small" ? "piccola taglia (max 5 kg)" : "altre taglie"}</span>}
                      </div>
                    )}
                    {b.notes && <p className="mt-3 rounded-sm bg-linen p-3 text-sm italic text-stone-600">“{b.notes}”</p>}

                    <div className="mt-5 flex flex-wrap gap-3 border-t border-stone-200 pt-4">
                      {b.status !== "confirmed" && (
                        <button
                          data-testid={`admin-confirm-${b.id}`}
                          onClick={() => setStatus(b.id, "confirmed")}
                          disabled={updating === b.id}
                          className="inline-flex items-center gap-2 rounded-full bg-emerald-700 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-transform duration-200 hover:scale-[1.03] disabled:opacity-50"
                        >
                          <Check size={13} /> Conferma
                        </button>
                      )}
                      {b.status !== "rejected" && (
                        <button
                          data-testid={`admin-reject-${b.id}`}
                          onClick={() => setStatus(b.id, "rejected")}
                          disabled={updating === b.id}
                          className="inline-flex items-center gap-2 rounded-full border border-red-300 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
                        >
                          <X size={13} /> Rifiuta
                        </button>
                      )}
                      {b.status !== "pending" && (
                        <button
                          data-testid={`admin-pending-${b.id}`}
                          onClick={() => setStatus(b.id, "pending")}
                          disabled={updating === b.id}
                          className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-50"
                        >
                          <Undo2 size={13} /> Rimetti in attesa
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}

            <div className="mt-16 border-t border-stone-300/50 pt-10" data-testid="photo-manager">
              <div className="flex flex-wrap items-baseline gap-x-4">
                <p className="font-serif text-2xl text-stone-900">Foto della galleria</p>
                {savingOrder && <span className="text-xs uppercase tracking-[0.15em] text-terracotta" data-testid="order-saving">Salvataggio ordine…</span>}
              </div>
              <p className="mt-2 max-w-xl text-sm font-light leading-relaxed text-stone-500">
                Carica qui le tue foto — anche più di una insieme. Vengono raddrizzate e ottimizzate in
                automatico per un caricamento veloce da mobile (JPG, PNG o WebP, max 8 MB ciascuna).
                <strong className="font-medium text-stone-600"> Trascina le miniature per riordinare la galleria pubblica.</strong>
              </p>

              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {photos.map((p, i) => (
                  <div
                    key={p.id}
                    data-testid={`photo-slot-${p.id}`}
                    draggable
                    onDragStart={() => { dragIndex.current = i; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => dropPhoto(i)}
                    className={`group relative cursor-grab overflow-hidden rounded-sm border border-stone-300/50 bg-white active:cursor-grabbing ${p.is_visible === false ? "opacity-50" : ""}`}
                  >
                    <img src={`${API}/photos/${p.id}/image`} alt={p.label || "Foto caricata"} className="pointer-events-none aspect-square w-full object-cover" loading="lazy" draggable={false} />
                    <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-stone-950/50 text-linen backdrop-blur-sm">
                      <GripVertical size={13} />
                    </span>
                    <span className="absolute bottom-2 left-2 rounded-full bg-stone-950/50 px-2 py-0.5 font-mono text-[10px] text-linen backdrop-blur-sm">
                      {i + 1}
                    </span>
                    {p.is_visible === false && (
                      <span className="absolute bottom-2 right-2 rounded-full bg-stone-950/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-linen">Nascosta</span>
                    )}
                    <button
                      data-testid={`photo-visibility-${p.id}`}
                      onClick={() => toggleVisible(p)}
                      className="absolute right-11 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-stone-950/60 text-linen opacity-0 backdrop-blur-sm transition-opacity duration-200 hover:bg-olive group-hover:opacity-100"
                      aria-label={p.is_visible === false ? "Mostra in galleria" : "Nascondi dalla galleria"}
                    >
                      {p.is_visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button
                      data-testid={`photo-delete-${p.id}`}
                      onClick={() => deletePhoto(p.id)}
                      className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-stone-950/60 text-linen opacity-0 backdrop-blur-sm transition-opacity duration-200 hover:bg-red-700 group-hover:opacity-100"
                      aria-label="Elimina foto"
                    >
                      <Trash2 size={14} />
                    </button>
                    {p.label && (
                      <p className="absolute bottom-0 left-0 right-0 truncate bg-stone-950/60 px-2 py-1 text-[11px] text-linen backdrop-blur-sm">{p.label}</p>
                    )}
                  </div>
                ))}

                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    data-testid="photo-upload-slot"
                    onClick={() => fileInput.current?.click()}
                    disabled={!!uploading}
                    className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed border-stone-300 text-stone-400 transition-colors duration-200 hover:border-terracotta hover:text-terracotta disabled:opacity-50"
                  >
                    {uploading ? <Loader2 size={22} className="animate-spin" /> : <ImagePlus size={22} />}
                    <span className="px-2 text-center text-[11px] font-medium uppercase tracking-[0.12em]">
                      {uploading ? `Caricamento ${uploading}…` : "Aggiungi foto"}
                    </span>
                    {!uploading && <span className="px-2 text-center text-[10px] normal-case tracking-normal text-stone-400">puoi selezionarne più di una insieme</span>}
                  </button>
                  <input
                    ref={fileInput}
                    data-testid="photo-upload-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => uploadPhotos(e.target.files)}
                  />
                  <input
                    data-testid="photo-caption-input"
                    type="text"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Didascalia (facoltativa)"
                    maxLength={80}
                    className="rounded-sm border border-stone-300 bg-white px-3 py-2 text-xs text-stone-700 outline-none focus:border-terracotta"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
